import React from 'react';
import { LinkProps as NextLinkProps } from 'next/link';
import clsx from 'clsx';
import useStyles from './styles';
import { ExternalLinkIcon, NextLinkComposed, resolveLinkRouting } from '../linkRouting';

export type BaseLinkPropTypes = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  'href'
> &
  Omit<NextLinkProps, 'href'> & {
    href: string;
    external?: boolean;
    showExternalIcon?: boolean;
    /** @deprecated Prefer ref. Kept for backward compatibility. */
    innerRef?: React.Ref<HTMLAnchorElement>;
  };

const BaseLink = React.forwardRef<HTMLAnchorElement, BaseLinkPropTypes>(
  function BaseLink(props, ref) {
    const {
      href,
      as,
      external: externalProp,
      showExternalIcon = true,
      children,
      target,
      rel,
      prefetch,
      replace,
      scroll,
      shallow,
      locale,
      innerRef,
      className,
      ...other
    } = props;

    const { classes } = useStyles();
    const linkRef = innerRef || ref;
    const {
      external,
      target: computedTarget,
      rel: computedRel,
    } = resolveLinkRouting({
      href,
      external: externalProp,
      target,
      rel,
    });
    const rootClassName = clsx(classes.root, className);

    if (external) {
      return (
        <a
          ref={linkRef}
          href={href}
          target={computedTarget}
          rel={computedRel}
          className={rootClassName}
          {...other}
        >
          {children}
          {showExternalIcon && <ExternalLinkIcon />}
        </a>
      );
    }

    return (
      <NextLinkComposed
        ref={linkRef}
        href={href}
        as={as}
        replace={replace}
        scroll={scroll}
        shallow={shallow}
        prefetch={prefetch}
        locale={locale}
        className={rootClassName}
        {...other}
      >
        {children}
      </NextLinkComposed>
    );
  },
);

BaseLink.displayName = 'BaseLink';

export default BaseLink;
